/**
 * Capability dependency engine.
 *
 *  - Build-time validation (`validateCatalogGraph`) is enforced by a jest test
 *    so a broken graph (missing ref, unsatisfied `requires`, or cycle) fails CI,
 *    never ships.
 *  - Runtime impact analysis (`computeDisableImpact` / `computeEnablePrerequisites`)
 *    powers the admin "if I disable X …" preview and matches the resolver's
 *    Pass-2 dependency closure exactly.
 *
 * The reverse index and topological order are derived once from the frozen
 * `CATALOG` and memoized at module scope — both O(V+E).
 */
import { CATALOG, type CapabilityNodeKey, isCapabilityNodeKey } from '@/lib/capabilities/catalog';

export type GraphIssueKind = 'missing_ref' | 'cycle' | 'provides_unsatisfied';

export interface GraphIssue {
  kind: GraphIssueKind;
  detail: string;
}

function nodeKeys(): CapabilityNodeKey[] {
  return Object.keys(CATALOG) as CapabilityNodeKey[];
}

/**
 * Detects dangling `dependsOn` refs, `requires` entries with no provider, and
 * cycles (3-colour DFS). Returns [] when the graph is valid.
 */
export function validateCatalogGraph(): GraphIssue[] {
  const issues: GraphIssue[] = [];
  const keys = new Set<string>(Object.keys(CATALOG));

  // 1) Missing dependsOn refs.
  for (const node of Object.values(CATALOG)) {
    for (const dep of node.dependsOn) {
      if (!keys.has(dep)) {
        issues.push({ kind: 'missing_ref', detail: `${node.key} -> ${dep}` });
      }
    }
  }

  // 2) Every `requires` satisfied by some node's `provides`.
  const providers = new Set<string>(
    Object.values(CATALOG).flatMap((n) => [...n.provides])
  );
  for (const node of Object.values(CATALOG)) {
    for (const req of node.requires) {
      if (!providers.has(req)) {
        issues.push({ kind: 'provides_unsatisfied', detail: `${node.key} requires ${req}` });
      }
    }
  }

  // 3) Cycle detection over dependsOn (0 = unvisited, 1 = on-stack, 2 = done).
  const color = new Map<string, 0 | 1 | 2>();
  const visit = (key: string, stack: string[]): void => {
    color.set(key, 1);
    const node = CATALOG[key as CapabilityNodeKey];
    for (const dep of node?.dependsOn ?? []) {
      if (!keys.has(dep)) continue; // missing refs already reported
      const c = color.get(dep) ?? 0;
      if (c === 1) {
        issues.push({ kind: 'cycle', detail: [...stack, key, dep].join(' -> ') });
      } else if (c === 0) {
        visit(dep, [...stack, key]);
      }
    }
    color.set(key, 2);
  };
  for (const key of keys) {
    if ((color.get(key) ?? 0) === 0) visit(key, []);
  }

  return issues;
}

// ── Memoized derived structures ────────────────────────────────────────────
let reverseIndexMemo: Map<CapabilityNodeKey, CapabilityNodeKey[]> | null = null;
let topoOrderMemo: CapabilityNodeKey[] | null = null;

/** key -> keys that directly `dependsOn` it. Memoized. */
export function buildReverseIndex(): Map<CapabilityNodeKey, CapabilityNodeKey[]> {
  if (reverseIndexMemo) return reverseIndexMemo;
  const reverse = new Map<CapabilityNodeKey, CapabilityNodeKey[]>();
  for (const node of Object.values(CATALOG)) {
    for (const dep of node.dependsOn) {
      if (!isCapabilityNodeKey(dep)) continue;
      const list = reverse.get(dep) ?? [];
      list.push(node.key as CapabilityNodeKey);
      reverse.set(dep, list);
    }
  }
  reverseIndexMemo = reverse;
  return reverse;
}

/**
 * Dependencies-first topological order (a node appears after all of its
 * `dependsOn`). Used by the resolver's Pass-2 so a single linear pass suffices.
 * Falls back to catalog order for any nodes involved in a cycle (which CI
 * forbids anyway). Memoized.
 */
export function topoOrder(): CapabilityNodeKey[] {
  if (topoOrderMemo) return topoOrderMemo;
  const order: CapabilityNodeKey[] = [];
  const color = new Map<string, 0 | 1 | 2>();
  const visit = (key: CapabilityNodeKey): void => {
    color.set(key, 1);
    for (const dep of CATALOG[key]?.dependsOn ?? []) {
      if (!isCapabilityNodeKey(dep)) continue;
      const c = color.get(dep) ?? 0;
      if (c === 0) visit(dep as CapabilityNodeKey);
    }
    color.set(key, 2);
    order.push(key);
  };
  for (const key of nodeKeys()) {
    if ((color.get(key) ?? 0) === 0) visit(key);
  }
  topoOrderMemo = order;
  return order;
}

export interface DisableImpact {
  directDependents: CapabilityNodeKey[];
  transitiveDependents: CapabilityNodeKey[];
}

/**
 * Nodes that would break if `target` were disabled — the transitive
 * reverse-dependency closure. O(V+E).
 */
export function computeDisableImpact(target: CapabilityNodeKey): DisableImpact {
  const reverse = buildReverseIndex();
  const direct = reverse.get(target) ?? [];
  const seen = new Set<CapabilityNodeKey>();
  const queue = [...direct];
  while (queue.length) {
    const key = queue.shift() as CapabilityNodeKey;
    if (seen.has(key)) continue;
    seen.add(key);
    for (const dependent of reverse.get(key) ?? []) {
      if (!seen.has(dependent)) queue.push(dependent);
    }
  }
  return { directDependents: [...direct], transitiveDependents: [...seen] };
}

/**
 * Prerequisites that must also be enabled to enable `target` — the forward
 * `dependsOn` closure (excludes the target itself). O(V+E).
 */
export function computeEnablePrerequisites(target: CapabilityNodeKey): CapabilityNodeKey[] {
  const seen = new Set<CapabilityNodeKey>();
  const queue = [...(CATALOG[target]?.dependsOn ?? [])];
  while (queue.length) {
    const key = queue.shift() as string;
    if (!isCapabilityNodeKey(key) || seen.has(key)) continue;
    seen.add(key);
    for (const dep of CATALOG[key].dependsOn) queue.push(dep);
  }
  return [...seen];
}

/** Test seam: clear memoized structures (used only in unit tests). */
export function __resetDependencyGraphMemo(): void {
  reverseIndexMemo = null;
  topoOrderMemo = null;
}
